import { useEffect, useState } from 'react';

import SpaceBetween from '@amzn/awsui-components-core/space-between';
import ToggleButton from '@amzn/awsui-components-core/toggle-button';

import { useToolbarActionService } from '../../contexts/ToolbarActionServiceContext';
import { ToolbarAction, ToolbarActionService } from '../../services/ToolbarActionService';
import { ActionPanelState } from '../v2-content-action-panel/ActionPanelTypes';
import styles from './HeaderToolbarActions.module.scss';

interface ToolbarSnapshot {
  readonly actions: readonly ToolbarAction[];
  readonly activePanel: ActionPanelState;
}

/**
 * Subscribes to `service.on('changed')` and returns the current toolbar
 * snapshot. Fallback for React 17 where `useSyncExternalStore` isn't
 * available.
 */
function useToolbarSnapshot(service: ToolbarActionService): ToolbarSnapshot {
  const [snapshot, setSnapshot] = useState<ToolbarSnapshot>(() => ({
    actions: service.actions,
    activePanel: service.activePanel,
  }));

  useEffect(() => {
    let isMounted = true;
    setSnapshot({ actions: service.actions, activePanel: service.activePanel });
    const unsubscribe = service.on('changed', () => {
      if (isMounted) {
        setSnapshot({ actions: service.actions, activePanel: service.activePanel });
      }
    });
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [service]);

  return snapshot;
}

/**
 * Renders toolbar action buttons in the application header.
 *
 * Subscribes to the ToolbarActionService via {@link useToolbarSnapshot} so
 * that buttons appear/disappear as the content editor page registers or
 * unregisters its actions. Renders nothing when no actions are registered
 * or when the service context is unavailable.
 */
export default function HeaderToolbarActions() {
  const service = useToolbarActionService();
  const { actions, activePanel } = useToolbarSnapshot(service);

  if (actions.length === 0) return null;

  return (
    <div className={styles.toolbarActions}>
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        {actions.map((action: ToolbarAction) => (
          <ToggleButton
            key={action.id}
            variant="icon"
            pressed={activePanel === action.id}
            disabled={action.disabled}
            disabledReason={action.disabledReason}
            iconName={action.icon}
            pressedIconName={action.icon}
            iconSvg={action.iconSvg}
            pressedIconSvg={action.iconSvg}
            ariaLabel={action.ariaLabel ?? action.tooltip}
            onChange={({ detail }) => (service.activePanel = detail.pressed ? action.id : null)}
          />
        ))}
      </SpaceBetween>
    </div>
  );
}
